import {
  Box,
  Flex,
  Heading,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Stack,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { CherryPayApi } from "../../api/models";
import DateUtil from "../../util/DateUtil";
import { ViewControl } from "../ViewControl/ViewControl";
import { AttendanceDrawer } from "./AttendanceDrawer";
import { CopyText } from "../Utilities/CopyText";
import { useBusinessContext } from "../../context/ModelContext";
import { useApiClient } from "../../hooks/useApiClient";
import { useToast } from "../../hooks/useToast";
import { useNavigate } from "react-router-dom";
import { ContextMenuIcon } from "../../styles/icons";
import EventUtil from "../../util/EventUtil";
import { MembershipFilterSummary } from "../MemberFilterQueryModal/MemberFilterSummary";

interface EventDetailCardsProps {
  event: CherryPayApi.Event;
  miniMode?: boolean;
}

export const EventDetailCard = ({ event, miniMode }: EventDetailCardsProps) => {
  const apiClient = useApiClient();
  const business = useBusinessContext();
  const businessId = business.BusinessId;
  const { showErrorToast } = useToast();
  const navigate = useNavigate();

  const [isAttendanceDrawerOpen, setIsAttendanceDrawerOpen] =
    React.useState(false);

  const [eventKpis, setEventKpis] =
    useState<CherryPayApi.EventAttendanceScanKpis | null>();
  const [eventKpisLoading, setEventKpisLoading] = useState(true);

  useEffect(() => {
    setEventKpis(null);
    setEventKpisLoading(true);

    if (business.BusinessId && event.id) {
      apiClient.getEventKpis(businessId, event.id).then((event) => {
        if (event.ok) {
          setEventKpis(event.data);
        } else {
          showErrorToast("Unable to load event KPIs. Please try again.");
        }
        setEventKpisLoading(false);
      });
    }
  }, [event.id, business.BusinessId]);

  // Format dates and times for display
  const formattedStartDate = DateUtil.getLocalDateOnlyFormatFromString(
    event.EventStartDate
  );
  const formattedEndDate = DateUtil.getLocalDateOnlyFormatFromString(
    event.EventEndDate
  );

  const formattedCheckInStartTime = DateUtil.formatTimeToAmPm(
    event.CheckInStartTime
  );
  const formattedCheckInEndTime = DateUtil.formatTimeToAmPm(
    event.CheckInEndTime
  );

  // Calculate event duration
  const startDate = new Date(event.EventStartDate);
  const endDate = new Date(event.EventEndDate);
  const durationDays = Math.ceil(
    (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24)
  );
  const durationText = durationDays === 1 ? "1 day" : `${durationDays} days`;

  const totalAttendance = String(eventKpis?.ValidScanCount ?? 0);

  const scanUrl = EventUtil.getEventScanUrl(event);

  const handleEditEvent = () => {
    navigate(`/businesses/${event.BusinessId}/events/${event.id}?edit=true`);
  };

  return (
    <Box position="relative" width="100%">
      {miniMode && (
        <Flex position="absolute" top="0" right="0" zIndex="1">
          <Menu>
            <MenuButton
              as={IconButton}
              aria-label="Options"
              icon={<ContextMenuIcon />}
              variant="ghost"
              size="sm"
            />
            <MenuList>
              <MenuItem onClick={handleEditEvent}>Edit Event</MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      )}
      <Stack w="100%" align="start" spacing={2} direction={["column", "row"]}>
        <VStack align="start" w="100%">
          <Heading variant="view-heading">Event Details</Heading>
          <VStack align="start" w="100%" spacing={6} pb={10}>
            <ViewControl label="Event Name:" value={event.EventName ?? "-"} />

            <ViewControl
              label="Event Location:"
              value={event.EventLocation ?? "-"}
            />

            <ViewControl
              label="Event Timezone:"
              value={event.TimeZoneId ?? "-"}
            />

            <ViewControl label="Event Duration:" value={durationText} />
          </VStack>
          <Heading variant="view-heading">
            Eligible Members for This Event
          </Heading>
          <MembershipFilterSummary
            queryString={event.MemberFilterQuery ?? ""}
          />
          <Heading variant="view-heading" pt={10}>
            Attendance
          </Heading>

          <VStack align="start" w="100%" spacing={6} pb={10}>
            {eventKpisLoading ? (
              <Spinner margin="4" />
            ) : (
              <>
                <ViewControl
                  label="Total Valid Scans:"
                  value={totalAttendance}
                />
                <Link
                  color="cherryUi.600"
                  onClick={() => setIsAttendanceDrawerOpen(true)}
                  cursor="pointer"
                >
                  {`View Attendance List >`}
                </Link>
              </>
            )}
            <Box>
              <CopyText text={scanUrl} variant="cherryButton" fontSize="sm">
                Copy Scan Url
              </CopyText>
            </Box>
          </VStack>
        </VStack>

        <VStack align="start" w="100%">
          <Heading variant="view-heading">Schedule</Heading>
          <VStack align="start" w="100%" spacing={6} pb={10}>
            <ViewControl
              label="Start Date:"
              value={formattedStartDate ?? "-"}
            />

            <ViewControl label="End Date:" value={formattedEndDate ?? "-"} />

            <ViewControl
              label="Check-in Start Time:"
              value={formattedCheckInStartTime ?? "-"}
            />

            <ViewControl
              label="Check-in End Time:"
              value={formattedCheckInEndTime ?? "-"}
            />
          </VStack>

          <Heading variant="view-heading">Status</Heading>
          <VStack align="start" w="100%" spacing={6} pb={10}>
            <ViewControl label="Event Status:" value={event.EventStatus} />
          </VStack>
        </VStack>
        {isAttendanceDrawerOpen && (
          <AttendanceDrawer
            isOpen={isAttendanceDrawerOpen}
            onClose={() => setIsAttendanceDrawerOpen(false)}
            businessId={event.BusinessId as string}
            eventConfigurationId={event?.id}
            totalScans={totalAttendance}
          />
        )}
      </Stack>
    </Box>
  );
};
